import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Step } from 'components/Wizard';
import { templateAutoFramePreferenceSelector } from 'redux/modules/entities/selectors';
import { showError } from 'redux/modules/notification';
import { goToCreate } from 'redux/modules/router';
import { Dispatch } from 'redux/types';
import { getAspectRatioValue } from 'utils/aspect-ratio';
import { millisToSec } from 'utils/time';
import { VideoWizardCustomizeStepView } from '../types';
import VideoWizardCustomizeStep, {
  VideoWizardCustomizeStepProps,
} from '../VideoWizardCustomizeStep';
import { ERROR_ID } from './constants';
import {
  audioDurationMillisSelector,
  VideoFlowAction,
  VideoFlowState,
} from './state';
import { VideoFlowStep } from './types';
import { getFramingMethod } from './utils';

type UseCustomizeStepProps = Pick<
  VideoWizardCustomizeStepProps,
  'onSubmit' | 'onTemplateSelect'
> & {
  defaultView?: VideoWizardCustomizeStepView;
  isTemplateLocked?: boolean;
  state: VideoFlowState;
  stateDispatch: React.Dispatch<VideoFlowAction>;
  templateId?: string;
};

export default function useCustomizeStep({
  defaultView = 'customize',
  isTemplateLocked,
  onSubmit,
  onTemplateSelect,
  state,
  stateDispatch,
  templateId,
}: UseCustomizeStepProps) {
  const dispatch = useDispatch<Dispatch>();
  const [view, setView] = useState<VideoWizardCustomizeStepView>(defaultView);

  const {
    aspectRatioName,
    clipEndMillis,
    clipStartMillis,
    isFullEpisode,
    language,
    mediaImportedArgs,
    objective,
    transcribe,
    videoSrc,
    videoSrcUrl,
  } = state;
  const customizeAudioDurationMillis = audioDurationMillisSelector(state);

  const autoFramePreference = useSelector(
    useMemo(() => templateAutoFramePreferenceSelector(templateId ?? ''), [
      templateId,
    ]),
  );

  const framingMethod = getFramingMethod({
    autoFramePreference,
    objective,
    aspectRatioName,
    outputDurationMillis: customizeAudioDurationMillis,
  });

  const handleTemplateSelect = useCallback(
    (newTemplateId?: string) => {
      setView(newTemplateId ? 'customize' : 'template');
      onTemplateSelect(newTemplateId);
    },
    [onTemplateSelect],
  );

  const handleError = useCallback(() => {
    dispatch(
      showError({
        message: 'There was an error creating your template',
        code: 'ER007',
        dismissAfterSec: 5,
        id: ERROR_ID,
      }),
    );

    dispatch(goToCreate());
  }, [dispatch]);

  const step: Step<VideoFlowStep.CUSTOMIZE> = {
    component: (
      <VideoWizardCustomizeStep
        aspectRatioName={aspectRatioName}
        audioClipDurationSec={millisToSec(customizeAudioDurationMillis)}
        ctaLabel={
          objective === 'SingleClip' ? 'create project' : 'get your clips'
        }
        defaults={{
          aspectRatio: getAspectRatioValue(aspectRatioName),
        }}
        features={{
          templates: isTemplateLocked ? 'hidden' : true,
          waveform: 'hidden',
          captions: true,
        }}
        isFullEpisode={isFullEpisode}
        mainVideoSrc={videoSrc}
        mediaImportedArgs={mediaImportedArgs}
        placeholderVideo={{
          src: videoSrcUrl,
          fileName: mediaImportedArgs?.title,
          trimStartMillis: clipStartMillis,
          trimEndMillis: clipEndMillis,
          framingMethod,
        }}
        templateId={templateId}
        transcription={{
          transcribe,
          language,
        }}
        onError={handleError}
        onIsAutoFrameChange={isAutoFrame =>
          stateDispatch({
            type: 'auto-frame-change',
            payload: { isAutoFrame },
          })
        }
        onTemplateSelect={handleTemplateSelect}
        onTranscriptionChange={transcription =>
          stateDispatch({
            type: 'transcription-change',
            payload: {
              transcribe: transcription.transcribe,
              language: transcription.language,
            },
          })
        }
        onChangeAspectRatio={newAspectRatioName =>
          stateDispatch({
            type: 'aspect-ratio-select',
            payload: { aspectRatioName: newAspectRatioName },
          })
        }
        onSubmit={onSubmit}
        view={view}
      />
    ),
    // when the default view is "template", that means we're showing the template
    // step and it makes sense to call this step "template". when the default view
    // is "customize", the template step becomes hidden unless the user accesses it
    // via the UCS, so the name "customize" also works towards keeping the template
    // step hidden
    name: defaultView === 'customize' ? 'Customize' : 'Template',
    renderCancelButton: () => null,
    renderNextButton: () => null,
    stepId: VideoFlowStep.CUSTOMIZE,
  };

  return {
    step,
  };
}
